import merge from 'lodash/merge';

import config from '../../config';
import { addStoryblokEntities, getStoryblokEntities } from '../../ducks/marketplaceData.duck';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import { isCordova } from '../../util/mobile/deviceDetection';
import { getStoryblokClient } from '../../util/storyblok';
import {
  getStoryDatasources,
  getStoryPresetComponents,
  isBlogStory,
  PRODUCTS_DATASOURCE,
} from '../../util/storyblokDatasources';
import { parse } from '../../util/urlHelpers';
import { searchListings } from '../SearchPage/SearchPage.duck';
import reducerRegistry from '../../ducks/reducerRegistry';

const env = process.env.REACT_APP_ENV;

// ================ Action types ================ //

const STORE_CACHE_VERSION = 'app/StoryblokPage/STORE_CACHE_VERSION';

const FETCH_STORYBLOK_STORY_REQUEST = 'app/StoryblokPage/FETCH_STORYBLOK_STORY_REQUEST';
const FETCH_STORYBLOK_STORY_SUCCESS = 'app/StoryblokPage/FETCH_STORYBLOK_STORY_SUCCESS';
const FETCH_STORYBLOK_STORY_ERROR = 'app/StoryblokPage/FETCH_STORYBLOK_STORY_ERROR';

const FETCH_STORYBLOK_BLOG_POST_STORIES_REQUEST =
  'app/StoryblokPage/FETCH_STORYBLOK_BLOG_POST_STORIES_REQUEST';
const FETCH_STORYBLOK_BLOG_POST_STORIES_SUCCESS =
  'app/StoryblokPage/FETCH_STORYBLOK_BLOG_POST_STORIES_SUCCESS';
const FETCH_STORYBLOK_BLOG_POST_STORIES_ERROR =
  'app/StoryblokPage/FETCH_STORYBLOK_BLOG_POST_STORIES_ERROR';

const FETCH_STORY_LISTINGS_REQUEST = 'app/StoryblokPage/FETCH_STORY_LISTINGS_REQUEST';
const FETCH_STORY_LISTINGS_SUCCESS = 'app/StoryblokPage/FETCH_STORY_LISTINGS_SUCCESS';
const FETCH_STORY_LISTINGS_ERROR = 'app/StoryblokPage/FETCH_STORY_LISTINGS_ERROR';

const FETCH_STORY_PRESETS_REQUEST = 'app/StoryblokPage/FETCH_STORY_PRESETS_REQUEST';
const FETCH_STORY_PRESETS_SUCCESS = 'app/StoryblokPage/FETCH_STORY_PRESETS_SUCCESS';
const FETCH_STORY_PRESETS_ERROR = 'app/StoryblokPage/FETCH_STORY_PRESETS_ERROR';

const SWITCH_STORYBLOK_STORY_REQUEST = 'app/StoryblokPage/SWITCH_STORYBLOK_STORY_REQUEST';
const SWITCH_STORYBLOK_STORY_SUCCESS = 'app/StoryblokPage/SWITCH_STORYBLOK_STORY_SUCCESS';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.StoryblokPage} */
const initialState = {
  cacheVersion: null,
  currentStory: null,
  blogPostStories: [],
  blogPostStoriesInProgress: false,
  blogPostStoriesPagination: {},
  blogPostStoriesPage: 1,
  fetchingInProgress: false,
  switchingInProgress: false,
  fetchStoryListingsInProgress: false,
  fetchStoryPresetsInProgress: false,
  error: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STORE_CACHE_VERSION:
      return { ...state, cacheVersion: payload };

    case FETCH_STORYBLOK_STORY_REQUEST:
      return { ...state, fetchingInProgress: true, error: null };
    case FETCH_STORYBLOK_STORY_SUCCESS:
      return { ...state, fetchingInProgress: false, currentStory: payload };
    case FETCH_STORYBLOK_STORY_ERROR:
      return { ...state, fetchingInProgress: false, error: payload };

    case FETCH_STORYBLOK_BLOG_POST_STORIES_REQUEST:
      if (state.blogPostStoriesPage !== payload) {
        return {
          ...state,
          blogPostStoriesPage: payload,
          blogPostStories: [],
          blogPostStoriesInProgress: true,
          error: null,
        };
      }
      return { ...state, blogPostStoriesInProgress: true, error: null };
    case FETCH_STORYBLOK_BLOG_POST_STORIES_SUCCESS:
      return {
        ...state,
        blogPostStories: payload.stories,
        blogPostStoriesPagination: payload.pagination,
        blogPostStoriesInProgress: false,
      };
    case FETCH_STORYBLOK_BLOG_POST_STORIES_ERROR:
      return { ...state, blogPostStoriesInProgress: false, error: payload };

    case FETCH_STORY_LISTINGS_REQUEST:
      return { ...state, fetchStoryListingsInProgress: true, error: null };
    case FETCH_STORY_LISTINGS_SUCCESS:
      return { ...state, fetchStoryListingsInProgress: false };
    case FETCH_STORY_LISTINGS_ERROR:
      return { ...state, fetchStoryListingsInProgress: false, error: payload };

    case FETCH_STORY_PRESETS_REQUEST:
      return { ...state, fetchStoryPresetsInProgress: true, error: null };
    case FETCH_STORY_PRESETS_SUCCESS:
      return { ...state, fetchStoryPresetsInProgress: false };
    case FETCH_STORY_PRESETS_ERROR:
      return { ...state, fetchStoryPresetsInProgress: false, error: payload };

    case SWITCH_STORYBLOK_STORY_REQUEST:
      return { ...state, switchingInProgress: true, error: null };
    case SWITCH_STORYBLOK_STORY_SUCCESS:
      return { ...state, switchingInProgress: false, currentStory: payload };

    default:
      return state;
  }
}
// ================ Action creators ================ //

export const storeCacheVersion = (cacheVersion) => ({
  type: STORE_CACHE_VERSION,
  payload: cacheVersion,
});

export const fetchStoryblokStoryPageRequest = () => ({ type: FETCH_STORYBLOK_STORY_REQUEST });
export const fetchStoryblokStoryPageSuccess = (story) => ({
  type: FETCH_STORYBLOK_STORY_SUCCESS,
  payload: story,
});
export const fetchStoryblokStoryPageError = (error) => ({
  type: FETCH_STORYBLOK_STORY_ERROR,
  payload: error,
});
export const fetchStoryblokBlogPostStoriesRequest = (page) => ({
  type: FETCH_STORYBLOK_BLOG_POST_STORIES_REQUEST,
  payload: page,
});

export const fetchStoryblokBlogPostStoriesSuccess = (stories, pagination) => ({
  type: FETCH_STORYBLOK_BLOG_POST_STORIES_SUCCESS,
  payload: { stories, pagination },
});
export const fetchStoryblokBlogPostStoriesError = (error) => ({
  type: FETCH_STORYBLOK_BLOG_POST_STORIES_ERROR,
  payload: error,
});

export const fetchStoryListingsRequest = () => ({ type: FETCH_STORY_LISTINGS_REQUEST });
export const fetchStoryListingsSuccess = (story) => ({
  type: FETCH_STORY_LISTINGS_SUCCESS,
});
export const fetchStoryListingsError = (error) => ({
  type: FETCH_STORY_LISTINGS_ERROR,
  payload: error,
});

export const fetchStoryPresetsRequest = () => ({ type: FETCH_STORY_PRESETS_REQUEST });
export const fetchStoryPresetsSuccess = () => ({
  type: FETCH_STORY_PRESETS_SUCCESS,
});
export const fetchStoryPresetsError = (error) => ({
  type: FETCH_STORY_PRESETS_ERROR,
  payload: error,
});

export const switchStoryblokStoryRequest = () => ({ type: SWITCH_STORYBLOK_STORY_REQUEST });
export const switchStoryblokStorySuccess = (story) => ({
  type: SWITCH_STORYBLOK_STORY_SUCCESS,
  payload: story,
});

// ================ Selectors ================ //

const baseSearchParams = {
  include: ['images'],
  'fields.listing': [
    'title',
    'geolocation',
    'price',
    'createdAt',
    'publicData.category',
    'publicData.locationPlace',
    'publicData.verified',
    'metadata.rating',
    'metadata.ratingCount',
  ],
  'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  'limit.images': 1,
  pub_hidden: false,
  perPage: isCordova() ? 50 : 100,
};

// ================ Thunks ================ //

export const checkIfStoryHasBlogArchive = (story, searchParams, locale) => async (dispatch) => {
  const storyHasBlogArchive = story.content?.content?.some((c) =>
    c?.general_items.some((item) =>
      item.general_content.some((gc) => gc.component === 'archive_blog')
    )
  );
  if (storyHasBlogArchive) {
    await Promise.all([dispatch(fetchStoryblokBlogPostStories(searchParams, 12, locale))]);
  }
};

export const checkIfStoryHasListingsToDisplay =
  (story, locale, urlSearchParams = null) =>
  async (dispatch) => {
    try {
      dispatch(fetchStoryListingsRequest());
      const storyNeedsToDisplayListings = story.content?.content?.some((c) =>
        c?.general_items?.some((item) =>
          item?.general_content?.some(
            (gc) =>
              gc.component === 'carousel_search' ||
              gc.component === 'search_result' ||
              gc.component === 'listing_card' ||
              (gc.component === 'carousel' &&
                gc?.general_items?.some((item) => item.component === 'listing_slide'))
          )
        )
      );
      if (storyNeedsToDisplayListings) {
        const listingIds = story.content?.content?.reduce((accGridSection, c) => {
          // grid_section
          return [
            ...accGridSection,
            ...c.general_items
              .reduce((accGridItem, item) => {
                // grid_item
                return [
                  ...accGridItem,
                  ...item.general_content
                    .reduce((accContent, gc) => {
                      // content
                      const foundIds = [];
                      if (gc.component === 'listing_card') {
                        gc.listingId && foundIds.push(gc.listingId);
                      } else if (gc.component === 'carousel') {
                        gc.general_items.forEach((carouselItem) => {
                          if (carouselItem.component === 'listing_slide') {
                            carouselItem.listing[0]?.listingId &&
                              foundIds.push(carouselItem.listing[0].listingId);
                          }
                        });
                      }
                      return [...accContent, ...foundIds];
                    }, [])
                    .flat(),
                ];
              }, [])
              .flat(),
          ];
        }, []);
        const queries = story.content?.content?.reduce((accGridSection, c) => {
          // grid_section
          return [
            ...accGridSection,
            ...c.general_items
              .reduce((accGridItem, item) => {
                // grid_item
                return [
                  ...accGridItem,
                  ...item.general_content
                    .reduce((accContent, gc) => {
                      // content

                      const foundQueries = [];
                      // Only `search_result` renders pagination, so only that one
                      // may consume the `page` param from the url.
                      if (gc.component === 'carousel_search') {
                        gc.query && foundQueries.push({ query: gc.query, isPaginated: false });
                      } else if (gc.component === 'search_result') {
                        gc.query && foundQueries.push({ query: gc.query, isPaginated: true });
                      }

                      return [...accContent, ...foundQueries];
                    }, [])
                    .flat(),
                ];
              }, [])
              .flat(),
          ];
        }, []);

        const pageFromUrl = parseInt(urlSearchParams?.get('page'), 10);
        const hasPageInUrl = Number.isInteger(pageFromUrl) && pageFromUrl > 1;

        const promises = [];
        if (listingIds.length > 0) {
          const searchParams = {
            ids: listingIds.join(','), // comma separated list of listing ids
            ...baseSearchParams,
          };
          promises.push(dispatch(searchListings(searchParams, locale)));
        }
        if (queries.length > 0) {
          for (const { query, isPaginated } of queries) {
            const searchParams = parse(query, {
              latlng: ['origin'],
              latlngBounds: ['bounds'],
            });
            const pageMaybe = isPaginated && hasPageInUrl ? { page: pageFromUrl } : {};
            promises.push(
              dispatch(
                searchListings({ ...searchParams, ...pageMaybe, ...baseSearchParams }, locale)
              )
            );
          }
        }
        await Promise.all(promises);
        dispatch(fetchStoryListingsSuccess());
      }
    } catch (e) {
      logError(e, 'check-if-story-has-listings-to-display-failed');
      dispatch(fetchStoryListingsError(e));
    }
  };

export const checkIfStoryHasPresetComponent = (story, locale) => async (dispatch) => {
  try {
    dispatch(fetchStoryPresetsRequest());
    const storyblokApi = getStoryblokClient();

    const { passPresetComponents, passTierPresetComponents, passBenefitsPresetComponents } =
      getStoryPresetComponents(story);
    const isBlogPage = isBlogStory(story);
    const uniqueDataSources = getStoryDatasources(story);

    // Fetch all required data sources concurrently
    const fetchDatasourceEntries = async (datasource) => {
      try {
        const getEntries = async (datasourceName) => {
          const perPage = 100; // Maximum number of entries per page
          let page = 1;
          let allEntries = [];
          let hasMoreEntries;

          do {
            const response = await storyblokApi.get('cdn/datasource_entries', {
              version: env === 'production' ? 'published' : 'draft',
              datasource: datasourceName,
              per_page: perPage,
              page: page,
              dimension: locale,
              fallback_lang: 'en',
            });

            const { datasource_entries } = response.data;
            allEntries = allEntries.concat(datasource_entries);

            // Determine if there are more entries to fetch
            hasMoreEntries = datasource_entries.length === perPage;
            page += 1;
          } while (hasMoreEntries);

          // Convert the entries into a key-value map
          const entries = allEntries.reduce((acc, entry) => {
            acc[entry.name] = entry.dimension_value || entry.value;
            return acc;
          }, {});

          return { datasource: datasourceName, entries };
        };

        if (datasource === PRODUCTS_DATASOURCE) {
          const result = await getEntries(datasource);
          const products = result.entries.products;
          if (!products) return { datasource, entries: {} };
          const productDatasources = products.split(',');
          return Promise.all(productDatasources.map(getEntries));
        } else {
          return getEntries(datasource);
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.warn(`Data source '${datasource}' not found.`);
        } else {
          logError(error, 'fetch-datasource-entries-failed');
        }
        // Return empty entries for missing data sources
        return { datasource, entries: {} };
      }
    };

    const resolvedDataSources = await Promise.all(uniqueDataSources.map(fetchDatasourceEntries));
    const fetchedDataSources = resolvedDataSources.flat();

    // Create a mapping of datasource names to their entries
    const datasourceEntriesMap = fetchedDataSources.reduce((acc, { datasource, entries }) => {
      acc[datasource] = entries;
      return acc;
    }, {});

    // Assign data sources to components
    if (passPresetComponents.length > 0) {
      passPresetComponents.forEach((component) => {
        if (component.dataSource === undefined) {
          component.dataSource = {};
        }
        // Assign only the dataSource relevant to this component
        component.dataSource = {
          ...component.dataSource,
          ...datasourceEntriesMap['pass-preset-data'],
        };
      });
    }

    if (passTierPresetComponents.length > 0) {
      passTierPresetComponents.forEach((component) => {
        if (component.dataSource === undefined) {
          component.dataSource = {};
        }
        // Assign the data sources for product1, product2, product3, product4 under their respective keys
        component.dataSource = {
          ...component.dataSource,
          ...Object.keys(datasourceEntriesMap)
            .filter((key) => key.startsWith('product'))
            .reduce((acc, key) => {
              acc[key] = datasourceEntriesMap[key];
              return acc;
            }, {}),
        };
      });
    }

    if (passBenefitsPresetComponents.length > 0) {
      passBenefitsPresetComponents.forEach((component) => {
        if (component.dataSource === undefined) {
          component.dataSource = {};
        }
        component.dataSource = {
          ...component.dataSource,
          ...datasourceEntriesMap['pass-vorteilswelt'],
        };
      });
    }

    if (isBlogPage) {
      if (story.content.dataSource === undefined) {
        story.content.dataSource = {};
      }
      story.content.dataSource = {
        ...story.content.dataSource,
        ...datasourceEntriesMap['blog-pass-preset'],
      };
    }
    dispatch(fetchStoryPresetsSuccess());
  } catch (e) {
    logError(e, 'check-if-story-has-preset-component-failed');
    dispatch(fetchStoryPresetsError(e));
  }
};

export const checkIfStoryHasHeyFlow = (story) => () => {
  const heyFlowComponent = story.content?.content
    ?.map((c) =>
      c?.general_items
        ?.map((item) => item?.general_content?.filter((gc) => gc.component === 'heyflow').flat())
        .flat()
    )
    .flat();

  story.hasHeyFlow = heyFlowComponent.length > 0;
};

export const bridgeUpdateStoryblokStory = (story, _, locale) => async (dispatch, getState) => {
  try {
    dispatch(fetchStoryblokStoryPageRequest());
    story.full_slug = `${locale}/${story.full_slug}`;
    const currentStory = getState().StoryblokPage.currentStory;
    story = merge(currentStory, story);
    dispatch(addStoryblokEntities({ data: { story } }));
    dispatch(fetchStoryblokStoryPageSuccess(story));
  } catch (e) {
    logError(e, 'bridge-update-storyblok-story-failed');
    if (e.status === 404) {
      dispatch(fetchStoryblokStoryPageSuccess(null));
    } else {
      dispatch(fetchStoryblokStoryPageError(e));
    }
  }
};

// Storyblok's CDN caches a story 404 with `s-maxage=604800`. A story published
// moments after such a miss therefore stays unreachable for a week at whichever
// edge holds the stale entry, while every other edge serves it fine. The client
// makes that permanent: it pins `cv` per process (`if (!params.cv) params.cv =
// cacheVersions[params.token]`) and only refreshes it from a *successful*
// response, so a request that 404s can never learn the newer `cv` that would
// address a different cache key. Retrying once with an explicit `cv` breaks out
// of both caches; an genuinely missing story still 404s on the retry.
const fetchStoryWithCacheBustOn404 = async (storyblokApi, slug, locale) => {
  const params = {
    version: env === 'production' ? 'published' : 'draft',
    language: locale === 'de' ? 'default' : locale,
    resolve_relations: ['archive_blog.general_categories', 'blog_post.categories'],
  };

  try {
    return await storyblokApi.get(`cdn/stories/${slug}`, params);
  } catch (error) {
    if (error.status !== 404) throw error;
    return await storyblokApi.get(`cdn/stories/${slug}`, { ...params, cv: Date.now() });
  }
};

export const fetchStoryblokPageStory =
  (pathObject, searchParams, locale) => async (dispatch, getState) => {
    const { fetchingInProgress, switchingInProgress } = getState().StoryblokPage;
    if (fetchingInProgress || switchingInProgress) return;

    const { slug } = pathObject;
    const getStoryblokStory = () => {
      const ref = { slug: `${locale === config.locale ? '' : `${locale}/`}${slug}`, type: 'page' };
      const story = getStoryblokEntities(getState().marketplaceData.storyblokEntities, ref);
      return story ? story : {};
    };

    const getStoryblokBlogPostStory = () => {
      const ref = {
        slug: `${locale === config.locale ? '' : `${locale}/`}${slug}`,
        type: 'blog_post',
      };
      const story = getStoryblokEntities(getState().marketplaceData.storyblokEntities, ref);
      return story ? story : {};
    };

    const storyFromState = getStoryblokStory();
    const blogPostFromState = getStoryblokBlogPostStory();
    const storyToUse = Object.keys(storyFromState).length > 0 ? storyFromState : blogPostFromState;

    if (Object.keys(storyToUse).length === 0) {
      dispatch(fetchStoryblokStoryPageRequest());
      try {
        const storyblokApi = getStoryblokClient();

        const pageResponse = await fetchStoryWithCacheBustOn404(storyblokApi, slug, locale);

        const story = pageResponse.data.story || null;

        if (story) {
          if (typeof window === 'undefined') {
            if (slug === 'blog')
              await dispatch(fetchStoryblokBlogPostStories(searchParams, 12, locale));
            await dispatch(checkIfStoryHasListingsToDisplay(story, locale, searchParams));
            await dispatch(checkIfStoryHasPresetComponent(story, locale));
            await dispatch(checkIfStoryHasHeyFlow(story));
          } else {
            if (slug === 'blog') dispatch(fetchStoryblokBlogPostStories(searchParams, 12, locale));
            dispatch(checkIfStoryHasListingsToDisplay(story, locale, searchParams));
            dispatch(checkIfStoryHasPresetComponent(story, locale));
            dispatch(checkIfStoryHasHeyFlow(story));
          }
          dispatch(fetchStoryblokStoryPageSuccess(story));
        }
        dispatch(addStoryblokEntities(pageResponse));
        return pageResponse;
      } catch (error) {
        if (error.status !== 404) {
          logError(error, 'fetch-storyblok-story-failed');
        }
        dispatch(fetchStoryblokStoryPageError(error));
      }
    } else {
      try {
        if (typeof window === 'undefined') {
          if (slug === 'blog')
            await dispatch(fetchStoryblokBlogPostStories(searchParams, 12, locale));
          await dispatch(checkIfStoryHasListingsToDisplay(storyToUse, locale, searchParams));
          await dispatch(checkIfStoryHasPresetComponent(storyToUse, locale));
          await dispatch(checkIfStoryHasHeyFlow(storyToUse));
        } else {
          if (slug === 'blog') dispatch(fetchStoryblokBlogPostStories(searchParams, 12, locale));
          dispatch(checkIfStoryHasListingsToDisplay(storyToUse, locale, searchParams));
          dispatch(checkIfStoryHasPresetComponent(storyToUse, locale));
          dispatch(checkIfStoryHasHeyFlow(storyToUse));
        }
        dispatch(switchStoryblokStorySuccess(storyToUse));
      } catch (error) {
        logError(error, 'switch-storyblok-story-failed');
        if (error.status === 404) {
          dispatch(fetchStoryblokStoryPageSuccess(null));
        } else {
          dispatch(fetchStoryblokStoryPageError(error));
        }
      }
    }
  };

export const fetchStoryblokBlogPostStories =
  (searchParams, perPage = 12, locale) =>
  async (dispatch, getState) => {
    const { blogPostStoriesInProgress } = getState().StoryblokPage;
    if (blogPostStoriesInProgress) return;

    try {
      const storyblokApi = getStoryblokClient();
      const page = searchParams.get('page') || 1;
      const category = searchParams.get('category') || null;

      dispatch(fetchStoryblokBlogPostStoriesRequest(page));

      let filterByCategoryMaybe = {};
      if (category) {
        const allCategories = Object.values(
          getState().marketplaceData.storyblokEntities.category || {}
        );
        filterByCategoryMaybe = {
          filter_query: {
            categories: {
              any_in_array: allCategories.find(
                (category) => category.slug === searchParams.get('category')
              ).uuid,
            },
          },
        };
      }

      const blogPostsResponse = await storyblokApi.get(`cdn/stories`, {
        version: env === 'production' ? 'published' : 'draft',
        language: locale === 'de' ? 'default' : locale,
        per_page: perPage,
        page: parseInt(page),
        content_type: 'blog_post',
        excluding_fields: 'general_content',
        sort_by: 'first_published_at:desc',
        ...filterByCategoryMaybe,
      });

      const blogPostStories = blogPostsResponse?.data?.stories || null;

      dispatch(
        fetchStoryblokBlogPostStoriesSuccess(blogPostStories, {
          perPage: blogPostsResponse.perPage,
          total: blogPostsResponse.total,
        })
      );
      dispatch(addStoryblokEntities(blogPostsResponse));
      return blogPostsResponse;
    } catch (e) {
      logError(e, 'fetch-storyblok-blog-post-stories-failed');
      dispatch(fetchStoryblokBlogPostStoriesError(e));
    }
  };

export const loadData = (params, search, match) => async (dispatch) => {
  const searchParams =
    typeof search === 'string'
      ? new URLSearchParams(search)
      : new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const { slug, '*': rest } = params;
  const locale = getLocaleFromPath(match.path);
  const reg = new RegExp(`^${locale}/`, 'g');
  const cleanedSlug = slug?.replace(reg, '').split('#')[0];

  const combinedSlug = `${cleanedSlug}${rest ? (Array.isArray(rest) ? `/${rest.join('/')}` : `/${rest}`) : ''}`;

  return await dispatch(
    fetchStoryblokPageStory(
      {
        slug: combinedSlug,
        rest,
      },
      searchParams,
      locale
    )
  );
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('StoryblokPage', reducer);
